import { useState, useCallback } from 'react';
import { departmentsAPI } from '../services/departments';
import { useApi } from '../../../hooks/api/useApi';

export function useDepartments() {
  const [departments, setDepartments] = useState([]);
  const [activeDepartments, setActiveDepartments] = useState([]);
  const [deactivatedDepartments, setDeactivatedDepartments] = useState([]);
  const [myDepartmentModules, setMyDepartmentModules] = useState([]);
  const [currentDepartment, setCurrentDepartment] = useState(null);
  
  const { loading, error, execute, clearError } = useApi();

  const fetchAllDepartments = useCallback(async (params = {}) => {
    const result = await execute(() => departmentsAPI.getAllDepartments(params));
    if (result.success) {
      setDepartments(result.data.departments || []);
    }
    return result;
  }, [execute]);

  const fetchActiveDepartments = useCallback(async () => {
    const result = await execute(() => departmentsAPI.getActiveDepartments());
    if (result.success) {
      setActiveDepartments(result.data.departments || []);
    }
    return result;
  }, [execute]);

  const fetchDeactivatedDepartments = useCallback(async () => {
    const result = await execute(() => departmentsAPI.getDeactivatedDepartments());
    if (result.success) {
      setDeactivatedDepartments(result.data.departments || []);
    }
    return result;
  }, [execute]);

  const fetchMyDepartmentModules = useCallback(async () => {
    const result = await execute(() => departmentsAPI.getMyDepartmentModules());
    if (result.success) {
      setMyDepartmentModules(result.data.modules || []);
    }
    return result;
  }, [execute]);

  const getDepartmentById = useCallback(async (departmentId) => {
    const result = await execute(() => departmentsAPI.getDepartmentById(departmentId));
    if (result.success) {
      setCurrentDepartment(result.data);
    }
    return result;
  }, [execute]);

  const createDepartment = useCallback(async (data) => {
    const result = await execute(() => departmentsAPI.createDepartment(data));
    if (result.success) {
      await fetchAllDepartments();
    }
    return result;
  }, [execute, fetchAllDepartments]);

  const updateDepartment = useCallback(async (departmentId, data) => {
    const result = await execute(() => departmentsAPI.updateDepartment(departmentId, data));
    if (result.success) {
      setDepartments(prev => prev.map(d =>
        d.department_id === departmentId ? { ...d, ...result.data } : d
      ));
      if (currentDepartment?.department_id === departmentId) {
        setCurrentDepartment(prev => ({ ...prev, ...result.data }));
      }
    }
    return result;
  }, [execute, currentDepartment]);

  const deleteDepartment = useCallback(async (departmentId) => {
    const result = await execute(() => departmentsAPI.deleteDepartment(departmentId));
    if (result.success) {
      setDepartments(prev => prev.filter(d => d.department_id !== departmentId));
    }
    return result;
  }, [execute]);

  const deactivateDepartment = useCallback(async (departmentId) => {
    const result = await execute(() => departmentsAPI.deactivateDepartment(departmentId));
    if (result.success) {
      await fetchAllDepartments();
    }
    return result;
  }, [execute, fetchAllDepartments]);

  // Only one department manages the global files, so making one the manager stops the other
  const setGlobalFilesDepartment = useCallback(async (departmentId, managesGlobalFiles) => {
    const result = await execute(() => departmentsAPI.setGlobalFilesDepartment(departmentId, managesGlobalFiles));
    if (result.success) {
      setDepartments(prev => prev.map(d => (d.department_id === departmentId
        ? { ...d, manages_global_files: managesGlobalFiles }
        : { ...d, manages_global_files: managesGlobalFiles ? false : d.manages_global_files })));
    }
    return result;
  }, [execute]);

  const getDepartmentModules = useCallback(async (departmentId) => {
    return await execute(() => departmentsAPI.getDepartmentModules(departmentId));
  }, [execute]);

  const enableModule = useCallback(async (departmentId, moduleId) => {
    return await execute(() => departmentsAPI.enableModule(departmentId, moduleId));
  }, [execute]);

  const disableModule = useCallback(async (departmentId, moduleId) => {
    return await execute(() => departmentsAPI.disableModule(departmentId, moduleId));
  }, [execute]);

  const resetOverdueTasks = useCallback(async (departmentId) => {
    return await execute(() => departmentsAPI.resetOverdueTasks(departmentId));
  }, [execute]);

  return {
    departments,
    activeDepartments,
    deactivatedDepartments,
    myDepartmentModules,
    currentDepartment,
    loading,
    error,
    clearError,
    fetchAllDepartments,
    fetchActiveDepartments,
    fetchDeactivatedDepartments,
    fetchMyDepartmentModules,
    getDepartmentById,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    deactivateDepartment,
    setGlobalFilesDepartment,
    getDepartmentModules,
    enableModule,
    disableModule,
    resetOverdueTasks,
  };
}